/**
 * useCampaignPanelControls — write-side companion to useCampaignDeepLinkPanel.
 *
 * The reader (useCampaignDeepLinkPanel) maps URL → Zustand; this hook provides
 * the three open/close actions that map both Zustand (instant open) AND URL
 * (persist across refresh) in one call.
 *
 * URL mapping mirrors the reader exactly so a written URL reloads identically:
 *   openInsights(row)                → ?panel=insights            (overview tab)
 *   openInsights(row, 'detailed')    → ?panel=insights&sub=detailed
 *   openInsights(row, 'creator-fit') → ?panel=insights&sub=creator-fit
 *   openInsights(row, 'brand-safety')→ ?panel=brand-safety        (no ?sub)
 *   openConversation(row)            → ?panel=conversation
 *   closePanels()                    → clears panel/creator/sub/thread
 *
 * setInsightsTab(tab) is the narrow tab-switch variant used inside
 * CreatorInsightsDrawer so the active tab also survives a refresh.
 *
 * No loop: opening writes the URL AND sets Zustand immediately. When the
 * reader's effect fires, it finds the row in `rows` and calls the same Zustand
 * setter — Zustand set is idempotent (same reference = no additional renders).
 * Closing clears both; the reader sees no params and does nothing.
 */
import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCampaignUiStore } from '@discovr/features';
import type { RosterRowRef, DeepAnalysisTab } from '@discovr/features';
import type { CampaignSearch } from './search';

export function useCampaignPanelControls() {
  const navigate = useNavigate();

  const setDeepAnalysisCreator = useCampaignUiStore((s) => s.setDeepAnalysisCreator);
  const setDeepAnalysisTab = useCampaignUiStore((s) => s.setDeepAnalysisTab);
  const setConvCreator = useCampaignUiStore((s) => s.setConvCreator);

  /**
   * Open the Analytics/Brand-Safety insights drawer.
   *
   * tab defaults to 'overview'. The panel/sub URL encoding mirrors the reader:
   *   'brand-safety' → panel='brand-safety', sub=undefined
   *   any other tab   → panel='insights',    sub=<tab> (or undefined for 'overview')
   */
  const openInsights = useCallback(
    (row: RosterRowRef, tab: DeepAnalysisTab = 'overview') => {
      // Instant open — no round-trip through the reader.
      setDeepAnalysisCreator(row);
      setDeepAnalysisTab(tab);

      // Keyless (shadow) creators have no username → can't be URL-keyed. Open
      // locally only; writing an empty ?creator= would just pollute the URL and
      // can't be restored on refresh anyway.
      const username = row.platform?.username?.trim();
      if (!username) return;

      const isBrandSafety = tab === 'brand-safety';
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          creator: username,
          panel: isBrandSafety ? 'brand-safety' : 'insights',
          sub: !isBrandSafety && tab !== 'overview' ? tab : undefined,
          // Clear conversation params to avoid conflicting state.
          thread: undefined,
        })),
      } as never);
    },
    [navigate, setDeepAnalysisCreator, setDeepAnalysisTab],
  );

  /**
   * Open the Conversation (selection thread) drawer.
   */
  const openConversation = useCallback(
    (row: RosterRowRef) => {
      setConvCreator(row);

      // Keyless (shadow) creators have no username → open locally only.
      const username = row.platform?.username?.trim();
      if (!username) return;

      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          creator: username,
          panel: 'conversation',
          sub: undefined,
          thread: undefined,
        })),
      } as never);
    },
    [navigate, setConvCreator],
  );

  /**
   * Close all global drawers and clear the related URL params.
   */
  const closePanels = useCallback(() => {
    setDeepAnalysisCreator(null);
    setConvCreator(null);

    navigate({
      to: '.',
      search: ((p: Partial<CampaignSearch>) => ({
        ...p,
        creator: undefined,
        panel: undefined,
        sub: undefined,
        thread: undefined,
      })),
    } as never);
  }, [navigate, setDeepAnalysisCreator, setConvCreator]);

  /**
   * Switch the active insights tab — sets Zustand AND updates the URL so the
   * selected tab survives a refresh. Used by CreatorInsightsDrawer's onTabChange.
   */
  const setInsightsTab = useCallback(
    (tab: DeepAnalysisTab) => {
      setDeepAnalysisTab(tab);

      const isBrandSafety = tab === 'brand-safety';
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          panel: isBrandSafety ? 'brand-safety' : 'insights',
          sub: !isBrandSafety && tab !== 'overview' ? tab : undefined,
          thread: undefined,
        })),
      } as never);
    },
    [navigate, setDeepAnalysisTab],
  );

  return { openInsights, openConversation, closePanels, setInsightsTab };
}
